import { useCallback } from 'react';

/**
 * Проверяет, является ли ошибка валидной ошибкой resend_limit (не истекло ли время)
 * @param {Object} errorData - Данные ошибки из storage
 * @param {string} email - Email пользователя (для получения longResendTimer)
 * @returns {boolean} - true если ошибка валидна (время не истекло), false в противном случае
 */
export const isResendLimitErrorValid = (errorData, email) => {
  if (!errorData || errorData.type !== 'resend_limit') {
    return false;
  }

  if (errorData.expiresAt) {
    const nowSec = Math.floor(Date.now() / 1000);
    return errorData.expiresAt > nowSec;
  }

  if (!email) {
    return false;
  }

  try {
    const longResendTimerKey = `longResendTimer_${email}`;
    const longResendTimerData = localStorage.getItem(longResendTimerKey);
    if (!longResendTimerData) {
      return false;
    }

    const timerData = JSON.parse(longResendTimerData);
    if (!timerData || !timerData.endTime) {
      return false;
    }

    const nowSec = Math.floor(Date.now() / 1000);
    return timerData.endTime > nowSec;
  } catch (error) {
    console.error('Error checking resend limit error validity:', error);
    return false;
  }
};

/**
 * Безопасно удаляет ошибку из storage, проверяя тип и время истечения
 * @param {string} email - Email пользователя
 * @param {string} errorKey - Ключ ошибки в storage (обычно 'error')
 */
export const removeErrorIfAllowed = (email, errorKey = 'error') => {
  if (!email) {
    return;
  }

  try {
    const storageKey = `${errorKey}_${email}`;
    const errorDataStr = localStorage.getItem(storageKey);
    if (!errorDataStr) {
      return;
    }

    const errorData = JSON.parse(errorDataStr);
    
    if (errorData && errorData.type === 'resend_limit') {
      if (isResendLimitErrorValid(errorData, email)) {
        return;
      }
    }

    localStorage.removeItem(storageKey);
  } catch (error) {
    console.error(`Error removing ${errorKey} from localStorage:`, error);
  }
};

export const clearAppStorageKeys = (excludeKeys = [], excludeEmail = null) => {
  const storageKeyPrefixes = [
    'error_',
    'attemptsLeft_',
    'longResendTimer_',
    'blockMessage_',
    'sessionData_',
    'continuationNotification_'
  ];

  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !storageKeyPrefixes.some(prefix => key.startsWith(prefix))) {
      continue;
    }

    if (key.startsWith('error_')) {
      const emailMatch = key.match(/^error_(.+)$/);
      if (emailMatch) {
        const errorEmail = emailMatch[1];
        try {
          const errorDataStr = localStorage.getItem(key);
          if (errorDataStr) {
            const errorData = JSON.parse(errorDataStr);
            if (isResendLimitErrorValid(errorData, errorEmail)) {
              continue;
            }
          }
        } catch (error) {
          console.error(`Error checking error data for ${key}:`, error);
        }
      }
    }

    let shouldExclude = false;
    if (excludeEmail && excludeKeys.length > 0) {
      const expectedSuffix = `_${excludeEmail}`;
      if (key.endsWith(expectedSuffix)) {
        const keyWithoutSuffix = key.substring(0, key.length - expectedSuffix.length);
        if (excludeKeys.includes(keyWithoutSuffix)) {
          shouldExclude = true;
        }
      }
    }

    if (!shouldExclude) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Error removing ${key} from localStorage:`, error);
    }
  });
};

export const useSessionStorage = (email) => {
  const getStorageKey = useCallback((key) => {
    if (!email) return null;
    return `${key}_${email}`;
  }, [email]);

  const get = useCallback((key) => {
    const storageKey = getStorageKey(key);
    if (!storageKey) return null;
    try {
      const item = localStorage.getItem(storageKey);
      if (!item) return null;
      
      const parsed = JSON.parse(item);
      
      if (key === 'error' && parsed && parsed.type === 'resend_limit') {
        if (!isResendLimitErrorValid(parsed, email)) {
          localStorage.removeItem(storageKey);
          return null;
        }
      }
      
      return parsed;
    } catch (error) {
      console.error(`Error reading ${key} from localStorage:`, error);
      return null;
    }
  }, [getStorageKey, email]);

  const set = useCallback((key, value) => {
    const storageKey = getStorageKey(key);
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(value));
    } catch (error) {
      console.error(`Error saving ${key} to localStorage:`, error);
    }
  }, [getStorageKey]);

  const remove = useCallback((key) => {
    const storageKey = getStorageKey(key);
    if (!storageKey) return;
    
    if (key === 'error') {
      if (email) {
        removeErrorIfAllowed(email, key);
      }
      return;
    }
    
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error(`Error removing ${key} from localStorage:`, error);
    }
  }, [getStorageKey, email]);
  
  const safeRemoveError = useCallback((errorKey = 'error') => {
    if (email) {
      removeErrorIfAllowed(email, errorKey);
    }
  }, [email]);

  const clearAll = useCallback(() => {
    if (!email) return;
    
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes(email) || key.includes('continuationNotification_'))) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.error(`Error removing ${key} from localStorage:`, error);
      }
    });
  }, [email]);

  const clearAppKeys = useCallback((excludeKeys = [], excludeEmail = null) => {
    clearAppStorageKeys(excludeKeys, excludeEmail);
  }, []);

  const setSessionData = useCallback((data) => {
    set('sessionData', {
      ...data,
      timestamp: Date.now()
    });
  }, [set]);

  const getSessionData = useCallback(() => {
    return get('sessionData');
  }, [get]);

  const hasSessionData = useCallback(() => {
    return !!get('sessionData');
  }, [get]);

  return {
    get,
    set,
    remove,
    clearAll,
    clearAppKeys,
    setSessionData,
    getSessionData,
    hasSessionData,
    removeErrorIfAllowed: safeRemoveError,
  };
};
